import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Button,
  CargoProvider,
  ErrorAlert,
  Logo,
  PasswordField,
  TextField,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { z } from 'zod';

import vesselIllustrationSrc from '../../../assets/vessel_illustration.png';
import vesselWithContainersSrc from '../../../assets/vessel_with_containers.jpg';
import { useAuthActions } from '../../../context/auth/auth-hooks';
import {
  type PlatformEnum,
  usePlatform,
} from '../../../context/PlatformContext';
import { useReCAPTCHA } from '../../../context/recaptcha/ReCaptchaProvider';
import { type AuthErrorType } from '../../../network/apis/auth/types';
import {
  type BaseErrorType,
  type ErrorResponse,
} from '../../../network/apis/types';
import { RICSignInRedirect } from '../../../router/tradeflow-login/RICSignInRedirect';
import { reportException } from '../../../utils/error-reporting/error-reporting';
import { getTheme } from '../../../utils/getTheme';
import { useTracking } from '../../../utils/snowplow/hooks';
import { MOBILE_MAX_WIDTH } from '../../constants';
import { useKeypressEffect } from '../../hooks/use-keypress-effect';
import { canWTWUserUseLoginPage, wtwLoginPageExceptionMessage } from '../utils';

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type SignInFormData = z.infer<typeof signInSchema>;

const SignInHeader = ({ theme }: { theme: PlatformEnum }) => {
  // For Breeze, show centered logo in header
  if (theme === 'breeze') {
    return (
      <div className="flex flex-col items-center space-y-4">
        <div className="grid items-center justify-center h-fit">
          <Logo theme={theme} variant="rebrandedLight" className="h-8" />
        </div>
        <div className="text-center space-y-2 py-1">
          <Typography level="h2">
            {theme === 'breeze' ? 'Welcome back!' : 'Welcome!'}
          </Typography>
        </div>
      </div>
    );
  }

  // For WTW, only show the title (logo will be positioned absolutely)
  return (
    <div className="text-center space-y-2 py-1">
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      <Typography level="h2">Welcome back!</Typography>
    </div>
  );
};

const SignInForm = ({
  setAuthenticationError,
  authenticationError,
}: {
  setAuthenticationError: (error: string | false) => void;
  authenticationError: string | false;
  theme: string;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const { login } = useAuthActions();
  const { verifyReCAPTCHA } = useReCAPTCHA();
  const { isWtw } = usePlatform();

  const showError = loading ? false : authenticationError && !isDismissed;

  const handleDismissError = () => {
    setIsDismissed(true);
    setAuthenticationError(false);
  };

  // Reset dismissed state when starting a new submission
  useEffect(() => {
    if (loading) {
      setIsDismissed(false);
    }
  }, [loading]);

  const setError = useCallback(
    (error: AuthErrorType | BaseErrorType) => {
      setIsDismissed(false);
      switch (error) {
        case 'IncorrectLoginException':
          setAuthenticationError('Incorrect email or password.');
          break;
        case 'WTWLoginException':
          setAuthenticationError(wtwLoginPageExceptionMessage);
          break;
        default:
          setAuthenticationError(
            'Something went wrong, please refresh and try again.',
          );
          break;
      }
    },
    [setAuthenticationError],
  );

  const form = useForm<SignInFormData, typeof zodValidator>({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmitInvalid: async () => {
      // Handle invalid form submission
      setAuthenticationError('Some fields are missing or invalid.');
    },
    onSubmit: async ({ value }) => {
      // Validate form values using the schema
      const validation = signInSchema.safeParse(value);

      if (!validation.success) {
        setAuthenticationError('Some fields are missing or invalid.');
        return;
      }

      if (!canWTWUserUseLoginPage(value.email, isWtw)) {
        setError('WTWLoginException');
        return;
      }

      if (authenticationError) {
        setAuthenticationError(false);
      }

      setLoading(true);

      try {
        const reCaptchaResult = await verifyReCAPTCHA('platform_sign_in');
        if (reCaptchaResult?.success) {
          await login?.(value);
        }
      } catch (error) {
        const { response } = error as ErrorResponse<AuthErrorType>;
        const { error_type } = response?.data ?? {};
        reportException(error);
        setError(error_type);
      } finally {
        setLoading(false);
      }
    },
    validatorAdapter: zodValidator,
  });

  useKeypressEffect('Enter', () => form.handleSubmit());

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-6 min-w-full"
    >
      {/* Email */}
      <form.Field
        name="email"
        validators={{
          onBlur: ({ value }) => {
            const result = signInSchema.shape.email.safeParse(value);
            return result.success ? undefined : result.error.issues[0]?.message;
          },
        }}
        children={(field) => (
          <TextField
            data-testid="email-input"
            label="Email Address"
            placeholder="example@gmail.com"
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
            isDisabled={loading}
            autoComplete="username"
            type="email"
          />
        )}
      />

      {/* Password */}
      <form.Field
        name="password"
        validators={{
          onBlur: ({ value }) => {
            const result = signInSchema.shape.password.safeParse(value);
            return result.success ? undefined : result.error.issues[0]?.message;
          },
        }}
        children={(field) => (
          <PasswordField
            data-testid="password-input"
            label="Password"
            placeholder="Password..."
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            errorMessage={field.state.meta.errors?.[0] || undefined}
            isInvalid={!!field.state.meta.errors?.length}
            isDisabled={loading}
            autoComplete="current-password"
          />
        )}
      />

      <div className="flex justify-end">
        <RouterLink
          to="/login/reset-password"
          className="text-gray-600 hover:underline text-sm"
          // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
        >
          Forgot your password?
        </RouterLink>
      </div>

      <form.Subscribe
        selector={(state) => [state.isSubmitting]}
        children={([isSubmitting]) => (
          <div className="pt-3">
            <Button
              type="submit"
              data-testid="submit"
              isDisabled={!!isSubmitting || loading}
              variant="primary"
              customStyles="w-full"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </div>
        )}
      />

      {/* Error Alert */}
      <div className="relative">
        {showError && (
          <ErrorAlert
            role="error-alert"
            message={authenticationError || ''}
            onDismiss={handleDismissError}
          />
        )}
      </div>
    </form>
  );
};

const MobileSignInPage = ({
  authenticationError,
  setAuthenticationError,
  currentYear,
  theme,
  backgroundImage,
}: {
  authenticationError: string | false;
  setAuthenticationError: (error: string | false) => void;
  currentYear: number;
  theme: PlatformEnum;
  backgroundImage: string;
}) => (
  <div
    className="absolute inset-0 grid place-items-center self-start p-4 max-h-full min-h-full w-full flex-grow bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: `url(${backgroundImage})` }}
  >
    {/* WTW Logo positioned absolutely like original SignIn */}
    {theme === 'wtw' && (
      <Logo
        variant="dark"
        theme="wtw"
        className="absolute top-9 left-9 w-auto h-30 w-30 z-10"
      />
    )}
    <div className="relative w-full max-h-full max-w-md bg-white/80 backdrop-blur-sm rounded-2xl py-5 px-8 shadow-xl flex flex-col gap-6">
      <SignInHeader theme={theme} />
      <SignInForm
        authenticationError={authenticationError}
        setAuthenticationError={setAuthenticationError}
        theme={theme}
      />
      {theme === 'breeze' && (
        <div className="text-center text-xs text-gray-500">
          {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
          © {currentYear} Breeze. All rights reserved.
        </div>
      )}
    </div>
  </div>
);

const DesktopSignInPage = ({
  authenticationError,
  setAuthenticationError,
  currentYear,
  theme,
  backgroundImage,
}: {
  authenticationError: string | false;
  setAuthenticationError: (error: string | false) => void;
  currentYear: number;
  theme: PlatformEnum;
  backgroundImage: string;
}) => (
  <div className="relative grid grid-cols-2 py-6 px-12 items-center bg-white w-full h-[100dvh] max-h-[100dvh]">
    {/* WTW Logo positioned absolutely like original SignIn */}
    {theme === 'wtw' && (
      <Logo
        variant="dark"
        theme="wtw"
        className="absolute top-9 left-9 w-auto h-30 w-30"
      />
    )}

    {/* Left Panel - Form */}
    <div className="grid place-items-center py-10 gap-5 px-20">
      <SignInHeader theme={theme} />
      <SignInForm
        authenticationError={authenticationError}
        setAuthenticationError={setAuthenticationError}
        theme={theme}
      />
    </div>

    {/* Right Panel - Illustration */}
    <div className="max-w-[602px] max-h-[718px]">
      <img
        src={backgroundImage}
        alt="Ship illustration"
        className="w-full h-full rounded-2xl"
      />
    </div>
    {/* Footer */}
    {theme === 'breeze' && (
      <div className="text-center text-sm text-gray-500">
        {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
        © {currentYear} Breeze. All rights reserved.
      </div>
    )}
  </div>
);

function PlatformSignIn() {
  useTracking({
    eventFeature: 'login',
    eventAction: 'page_view',
    eventTrigger: 'sign_in',
  });

  const [authenticationError, setAuthenticationError] = useState<
    string | false
  >(false);

  const currentYear = new Date().getFullYear();
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });
  const theme = getTheme();
  const { isWtw } = usePlatform();

  const backgroundImage = isWtw
    ? vesselWithContainersSrc
    : vesselIllustrationSrc;

  return (
    <CargoProvider theme={theme}>
      <div data-testid="sign-in-page">
        {isMobile ? (
          <MobileSignInPage
            authenticationError={authenticationError}
            setAuthenticationError={setAuthenticationError}
            currentYear={currentYear}
            theme={theme}
            backgroundImage={backgroundImage}
          />
        ) : (
          <DesktopSignInPage
            authenticationError={authenticationError}
            setAuthenticationError={setAuthenticationError}
            currentYear={currentYear}
            theme={theme}
            backgroundImage={backgroundImage}
          />
        )}
      </div>
    </CargoProvider>
  );
}

export const SignIn = () => {
  const [searchParams] = useSearchParams();
  const gcId = searchParams.get('gcid');

  const { isWtw } = usePlatform();

  return isWtw && gcId ? <RICSignInRedirect gcId={gcId} /> : <PlatformSignIn />;
};
